import React from 'react';
import {
    Box,
    TextInput,
    Select,
    Button,
    Text,
    SimpleGrid,
    ActionIcon,
    Paper,
    Accordion,
    Alert,
    Badge,
    Group
} from '@mantine/core';
import {
    IconChevronDown,
    IconPlus,
    IconTrash,
    IconArrowsShuffle
} from '@tabler/icons-react';

export interface FilterCondition {
    id: string;
    field: string;
    operator: string;
    value: string;
    function?: string;
}

export interface FilterGroup {
    id: string;
    operator: 'and' | 'or';
    conditions: FilterCondition[];
    groups: FilterGroup[];
}

export interface FieldOption {
    value: string;
    label: string;
}

export interface ScimFilterBuilderProps {
    rootGroup: FilterGroup;
    fieldOptions: FieldOption[];
    onUpdateFilter: (rootGroup: FilterGroup) => void;
    showFunctions?: boolean;
    customValueFields?: Record<string, FieldOption[]>;
    entityType?: string;
}

const OPERATOR_OPTIONS = [
    { value: 'eq', label: 'Equals (eq)' },
    { value: 'ne', label: 'Not Equals (ne)' },
    { value: 'co', label: 'Contains (co)' },
    { value: 'sw', label: 'Starts With (sw)' },
    { value: 'ew', label: 'Ends With (ew)' },
    { value: 'gt', label: 'Greater Than (gt)' },
    { value: 'ge', label: 'Greater or Equal (ge)' },
    { value: 'lt', label: 'Less Than (lt)' },
    { value: 'le', label: 'Less or Equal (le)' },
    { value: 'pr', label: 'Present (pr)' },
    { value: 'npr', label: 'Not Present (npr)' },
    { value: '$in', label: 'In ($in)' }
];

const FUNCTION_OPTIONS = [
    { value: '', label: 'No Function' },
    { value: 'length', label: 'Length' },
    { value: '$cnt', label: 'Count ($cnt)' }
];

export const ScimFilterBuilder: React.FC<ScimFilterBuilderProps> = ({
    rootGroup,
    fieldOptions,
    onUpdateFilter,
    showFunctions = true,
    customValueFields = {},
    entityType
}) => {
    const normalizeFieldKey = (field: string): string => {
        const raw = (field || '').trim();
        const noPrefix = raw.startsWith('$.') ? raw.slice(2) : raw;
        return noPrefix.toLowerCase();
    };

    // Helper function to check if a field is valid for the current entity type
    const isFieldValidForEntity = (field: string): boolean => {
        if (!entityType || !fieldOptions.length) return true; // If no entity type or no field options, assume valid
        const key = normalizeFieldKey(field);
        return fieldOptions.some(option => normalizeFieldKey(option.value) === key);
    };

    // Helper function to get invalid fields in a filter group
    const getInvalidFields = (group: FilterGroup): string[] => {
        const invalidFields: string[] = [];

        // Check conditions
        group.conditions.forEach(condition => {
            if (condition.field && !isFieldValidForEntity(condition.field)) {
                invalidFields.push(condition.field);
            }
        });

        // Check nested groups
        group.groups.forEach(nestedGroup => {
            invalidFields.push(...getInvalidFields(nestedGroup));
        });

        return [...new Set(invalidFields)]; // Remove duplicates
    };

    const invalidFields = getInvalidFields(rootGroup);

    // Collect all condition.field values from the tree (for pasted filters with unknown attributes)
    const collectFieldValues = (group: FilterGroup): string[] => {
        const fields = group.conditions
            .map(c => c.field)
            .filter((f): f is string => !!f && f.trim() !== '');
        return [
            ...fields,
            ...group.groups.flatMap(g => collectFieldValues(g))
        ];
    };
    const allFieldsInFilter = [...new Set(collectFieldValues(rootGroup))];
    // Extend field options so pasted/unrecognized attribute names appear in the selector (use API-style names, no $. prefix)
    const knownValues = new Set(fieldOptions.map(o => normalizeFieldKey(o.value)));
    const extraOptions: FieldOption[] = allFieldsInFilter
        .map(f => (f?.startsWith('$.') ? f.slice(2) : f))
        .filter((f): f is string => !!f && !knownValues.has(normalizeFieldKey(f)))
        .map(f => ({ value: f, label: `${f} (from filter)` }));
    const effectiveFieldOptions = [...fieldOptions, ...extraOptions];

    // Generate unique IDs
    const generateId = () => `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add condition to group
    const addCondition = (groupId: string) => {
        const newCondition: FilterCondition = {
            id: generateId(),
            field: fieldOptions[0]?.value || '',
            operator: 'eq',
            value: ''
        };

        const updatedGroup = addConditionToGroup(rootGroup, groupId, newCondition);
        onUpdateFilter(updatedGroup);
    };

    // Add group to parent group
    const addGroup = (parentGroupId: string) => {
        const newGroup: FilterGroup = {
            id: generateId(),
            operator: 'and',
            conditions: [],
            groups: []
        };

        const updatedGroup = addGroupToGroup(rootGroup, parentGroupId, newGroup);
        onUpdateFilter(updatedGroup);
    };

    // Generate random filter condition
    const generateRandomCondition = (): FilterCondition => {
        if (fieldOptions.length === 0) {
            // Fallback if no fields available
            return {
                id: generateId(),
                field: '',
                operator: 'eq',
                value: ''
            };
        }

        // Pick a random field
        const randomField = fieldOptions[Math.floor(Math.random() * fieldOptions.length)];
        
        // Randomly decide if we should use a function (including $cnt)
        const useFunction = Math.random() > 0.7; // 30% chance
        let randomFunction: string | undefined = undefined;
        let randomOperator: string;
        let randomValue: string = '';
        
        // Pick a random operator (including $in)
        const operators = ['eq', 'ne', 'co', 'sw', 'ew', 'gt', 'ge', 'lt', 'le', 'pr', '$in'];
        randomOperator = operators[Math.floor(Math.random() * operators.length)];
        
        if (randomOperator === '$in') {
            // For $in operator, generate comma-separated values
            const valueCount = Math.floor(Math.random() * 3) + 2; // 2-4 values
            const values: string[] = [];
            for (let i = 0; i < valueCount; i++) {
                values.push(`value${i + 1}`);
            }
            randomValue = values.join(', ');
        } else if (randomOperator === 'pr') {
            randomValue = ''; // No value needed for 'pr'
        } else if (randomOperator === 'gt' || randomOperator === 'ge' || randomOperator === 'lt' || randomOperator === 'le') {
            // Numeric operators
            randomValue = String(Math.floor(Math.random() * 100));
        } else {
            // String operators
            const sampleValues = ['Test', 'Sample', 'Example', 'Value', 'Data', 'Active', 'Enabled'];
            randomValue = sampleValues[Math.floor(Math.random() * sampleValues.length)];
        }
        
        if (useFunction) {
            const functions = ['length', '$cnt'];
            randomFunction = functions[Math.floor(Math.random() * functions.length)];
            // If using $cnt, use numeric operators and values
            if (randomFunction === '$cnt') {
                const cntOperators = ['eq', 'ne', 'gt', 'ge', 'lt', 'le'];
                randomOperator = cntOperators[Math.floor(Math.random() * cntOperators.length)];
                randomValue = String(Math.floor(Math.random() * 5)); // 0-4
            }
        }

        return {
            id: generateId(),
            field: randomField.value,
            operator: randomOperator,
            value: randomValue,
            function: randomFunction
        };
    };

    // Generate random filter (adds a new condition)
    const generateRandomFilter = () => {
        const randomCondition = generateRandomCondition();
        const updatedGroup = addConditionToGroup(rootGroup, rootGroup.id, randomCondition);
        onUpdateFilter(updatedGroup);
    };

    // Regenerate the last condition with random values
    const regenerateLastCondition = () => {
        if (rootGroup.conditions.length === 0) {
            // If no conditions, just add a new one
            generateRandomFilter();
            return;
        }

        const randomCondition = generateRandomCondition();
        // Replace the last condition in the root group
        const updatedGroup = {
            ...rootGroup,
            conditions: rootGroup.conditions.map((c, idx) =>
                idx === rootGroup.conditions.length - 1 ? randomCondition : c
            )
        };
        onUpdateFilter(updatedGroup);
    };

    // Remove item from group
    const removeItem = (groupId: string, itemId: string, isGroup: boolean = false) => {
        const updatedGroup = removeItemFromGroup(rootGroup, groupId, itemId, isGroup);
        onUpdateFilter(updatedGroup);
    };

    // Update condition
    const updateCondition = (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
        const updatedGroup = updateConditionInGroup(rootGroup, groupId, conditionId, updates);
        onUpdateFilter(updatedGroup);
    };

    // Update group operator
    const updateGroupOperator = (groupId: string, operator: 'and' | 'or') => {
        const updatedGroup = updateGroupOperatorInGroup(rootGroup, groupId, operator);
        onUpdateFilter(updatedGroup);
    };

    // Render condition
    const renderCondition = (condition: FilterCondition, groupId: string) => {
        const hasCustomValueField = condition.field in customValueFields;
        const customOptions = hasCustomValueField ? customValueFields[condition.field] : [];

        return (
            <Paper key={condition.id} withBorder p="md" mb="sm">
                <SimpleGrid cols={{ base: 1, xs: 12 }} spacing="sm" verticalSpacing="sm">
                    <Box style={{ gridColumn: '1 / -1' }}>
                        <Group align="flex-end" wrap="wrap" gap="sm">
                            <Select
                                label="Field"
                                size="sm"
                                flex={1}
                                miw={150}
                                value={condition.field?.startsWith('$.') ? condition.field.slice(2) : condition.field}
                                onChange={(val) => val != null && updateCondition(groupId, condition.id, { field: val })}
                                data={effectiveFieldOptions}
                            />
                            {showFunctions && (
                                <Select
                                    label="Function"
                                    size="sm"
                                    miw={120}
                                    value={condition.function || ''}
                                    onChange={(val) => updateCondition(groupId, condition.id, { function: val || undefined })}
                                    data={FUNCTION_OPTIONS}
                                />
                            )}
                            <Select
                                label="Operator"
                                size="sm"
                                miw={140}
                                value={condition.operator}
                                onChange={(val) => val != null && updateCondition(groupId, condition.id, { operator: val })}
                                data={OPERATOR_OPTIONS}
                            />
                            <Box flex={1} miw={180}>
                                {condition.operator === 'pr' || condition.operator === 'npr' ? (
                                    <>
                                        <TextInput
                                            size="sm"
                                            label="Value"
                                            value={condition.value}
                                            disabled
                                            placeholder={`Value not used for '${condition.operator === 'pr' ? 'Present' : 'Not Present'}' operator`}
                                            description={`The '${condition.operator === 'pr' ? 'Present' : 'Not Present'}' operator checks if a field ${condition.operator === 'pr' ? 'exists' : 'does not exist'}, value is ignored.`}
                                        />
                                    </>
                                ) : condition.operator === '$in' ? (
                                    <>
                                        <TextInput
                                            size="sm"
                                            label="Value"
                                            value={condition.value}
                                            onChange={(e) => updateCondition(groupId, condition.id, { value: e.target.value })}
                                            placeholder="Enter comma-separated values (e.g., 3155,3166)"
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            In operator checks if field value is in a list. Enter comma-separated values (e.g., 3155,3166). Result: {condition.field || 'field'} $in ["value1", "value2"]
                                        </Text>
                                    </>
                                ) : hasCustomValueField ? (
                                    <Select
                                        label="Value"
                                        size="sm"
                                        value={condition.value}
                                        onChange={(val) => val != null && updateCondition(groupId, condition.id, { value: val })}
                                        data={customOptions}
                                    />
                                ) : (
                                    <>
                                        <TextInput
                                            size="sm"
                                            label="Value"
                                            value={condition.value}
                                            onChange={(e) => updateCondition(groupId, condition.id, { value: e.target.value })}
                                            placeholder="Enter value..."
                                            description={
                                                condition.function === '$cnt' 
                                                    ? 'Count function: Use numeric values like "0" to check if field is not present, or "1", "2", etc. to check count.'
                                                    : undefined
                                            }
                                        />
                                        {condition.function === '$cnt' && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                Example: Use "eq 0" to check if field is not present, or "gt 0" to check if field has values.
                                            </Text>
                                        )}
                                    </>
                                )}
                            </Box>
                            <ActionIcon
                                color="red"
                                onClick={() => removeItem(groupId, condition.id)}
                                size="sm"
                                variant="subtle"
                            >
                                <IconTrash size={18} />
                            </ActionIcon>
                        </Group>
                    </Box>
                </SimpleGrid>
            </Paper>
        );
    };

    // Render group
    const renderGroup = (group: FilterGroup, depth: number = 0): React.ReactNode => (
        <Accordion key={group.id} defaultValue={group.id} mb="sm" ml={depth * 16}>
            <Accordion.Item value={group.id}>
                <Accordion.Control chevron={<IconChevronDown size={18} />}>
                    <Group align="center" gap="sm">
                        <Text size="sm" fw={500}>
                            Group ({group.conditions.length} conditions, {group.groups.length} sub-groups)
                        </Text>
                        <Select
                            size="xs"
                            value={group.operator}
                            onChange={(val) => val != null && updateGroupOperator(group.id, val as 'and' | 'or')}
                            data={[
                                { value: 'and', label: 'AND' },
                                { value: 'or', label: 'OR' }
                            ]}
                            miw={100}
                        />
                        <ActionIcon
                            component="div"
                            role="button"
                            tabIndex={0}
                            color="red"
                            onClick={(e) => { e.stopPropagation(); removeItem(group.id, group.id, true); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); removeItem(group.id, group.id, true); } }}
                            size="sm"
                            variant="subtle"
                        >
                            <IconTrash size={18} />
                        </ActionIcon>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Box>
                        {group.conditions.map(condition => renderCondition(condition, group.id))}
                        {group.groups.map(subGroup => renderGroup(subGroup, depth + 1))}
                        <Group gap="xs" mt="md">
                            <Button
                                leftSection={<IconPlus size={18} />}
                                onClick={() => addCondition(group.id)}
                                variant="outline"
                                size="sm"
                            >
                                Add Condition
                            </Button>
                            <Button
                                leftSection={<IconPlus size={18} />}
                                onClick={() => addGroup(group.id)}
                                variant="outline"
                                size="sm"
                            >
                                Add Group
                            </Button>
                        </Group>
                    </Box>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );

    // Helper functions for updating the filter structure
    const addConditionToGroup = (group: FilterGroup, targetGroupId: string, condition: FilterCondition): FilterGroup => {
        if (group.id === targetGroupId) {
            return { ...group, conditions: [...group.conditions, condition] };
        }
        return {
            ...group,
            groups: group.groups.map(subGroup => addConditionToGroup(subGroup, targetGroupId, condition))
        };
    };

    const addGroupToGroup = (group: FilterGroup, targetGroupId: string, newGroup: FilterGroup): FilterGroup => {
        if (group.id === targetGroupId) {
            return { ...group, groups: [...group.groups, newGroup] };
        }
        return {
            ...group,
            groups: group.groups.map(subGroup => addGroupToGroup(subGroup, targetGroupId, newGroup))
        };
    };

    const removeItemFromGroup = (group: FilterGroup, targetGroupId: string, itemId: string, isGroup: boolean): FilterGroup => {
        if (group.id === targetGroupId) {
            if (isGroup) {
                return { ...group, groups: group.groups.filter(g => g.id !== itemId) };
            } else {
                return { ...group, conditions: group.conditions.filter(c => c.id !== itemId) };
            }
        }
        return {
            ...group,
            groups: group.groups.map(subGroup => removeItemFromGroup(subGroup, targetGroupId, itemId, isGroup))
        };
    };

    const updateConditionInGroup = (group: FilterGroup, targetGroupId: string, conditionId: string, updates: Partial<FilterCondition>): FilterGroup => {
        if (group.id === targetGroupId) {
            return {
                ...group,
                conditions: group.conditions.map(condition =>
                    condition.id === conditionId ? { ...condition, ...updates } : condition
                )
            };
        }
        return {
            ...group,
            groups: group.groups.map(subGroup => updateConditionInGroup(subGroup, targetGroupId, conditionId, updates))
        };
    };

    const updateGroupOperatorInGroup = (group: FilterGroup, targetGroupId: string, operator: 'and' | 'or'): FilterGroup => {
        if (group.id === targetGroupId) {
            return { ...group, operator };
        }
        return {
            ...group,
            groups: group.groups.map(subGroup => updateGroupOperatorInGroup(subGroup, targetGroupId, operator))
        };
    };

    return (
        <Box>
            {/* Warning for invalid fields */}
            {invalidFields.length > 0 && (
                <Alert color="yellow" mb="sm">
                    <Text size="sm" mb="xs">
                        The following fields are not valid for the {entityType} entity type:
                    </Text>
                    <Group gap={4} wrap="wrap">
                        {invalidFields.map(field => (
                            <Badge
                                key={field}
                                size="sm"
                                color="yellow"
                                variant="outline"
                            >
                                {field}
                            </Badge>
                        ))}
                    </Group>
                </Alert>
            )}

            {renderGroup(rootGroup)}

            {/* Add initial condition/group buttons */}
            {rootGroup.conditions.length === 0 && rootGroup.groups.length === 0 && (
                <Group gap="xs" mt="md">
                    <Button
                        leftSection={<IconPlus size={18} />}
                        onClick={() => addCondition(rootGroup.id)}
                        variant="outline"
                        size="sm"
                    >
                        Add Condition
                    </Button>
                    <Button
                        leftSection={<IconPlus size={18} />}
                        onClick={() => addGroup(rootGroup.id)}
                        variant="outline"
                        size="sm"
                    >
                        Add Group
                    </Button>
                    {fieldOptions.length > 0 && (
                        <Button
                            leftSection={<IconArrowsShuffle size={18} />}
                            onClick={generateRandomFilter}
                            variant="outline"
                            size="sm"
                            color="gray"
                        >
                            Generate Random Filter
                        </Button>
                    )}
                </Group>
            )}

            {/* Random filter buttons - always visible when fields are available */}
            {fieldOptions.length > 0 && (rootGroup.conditions.length > 0 || rootGroup.groups.length > 0) && (
                <Group gap="xs" mt="md">
                    <Button
                        leftSection={<IconArrowsShuffle size={18} />}
                        onClick={generateRandomFilter}
                        variant="outline"
                        size="sm"
                        color="gray"
                    >
                        Add Random Condition
                    </Button>
                    {rootGroup.conditions.length > 0 && (
                        <Button
                            leftSection={<IconArrowsShuffle size={18} />}
                            onClick={regenerateLastCondition}
                            variant="outline"
                            size="sm"
                            color="gray"
                        >
                            Regenerate Last Condition
                        </Button>
                    )}
                </Group>
            )}
        </Box>
    );
};
